/**
 * Calculates the relative luminance of a hex color.
 * Formula based on WCAG 2.x standards.
 */
function getLuminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const [R, G, B] = [r, g, b].map((v) =>
    v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4),
  );

  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

/**
 * Returns a contrasting text color (white or dark slate) for a given background hex color.
 */
export function getContrastTextColor(hexColor: string | undefined): string {
  if (!hexColor || !hexColor.startsWith("#")) return "#1e293b"; // Default dark text

  // Normalize 3-digit hex to 6-digit
  let hex = hexColor;
  if (hex.length === 4) {
    hex = "#" + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
  }

  try {
    const luminance = getLuminance(hex);
    // WCAG threshold is 0.179, but 0.5 is often better for subjective "dark vs light"
    return luminance > 0.5 ? "#1e293b" : "#ffffff";
  } catch {
    return "#1e293b";
  }
}

/**
 * Ensures a color is dark enough to be readable on a white/light background.
 * If contrast ratio is poor, it dynamically darkens the color.
 */
export function getSafeTextColor(
  hexColor: string | undefined,
  defaultFallback = "#0f172a",
): string {
  if (!hexColor || !hexColor.startsWith("#")) return defaultFallback;

  let hex = hexColor;
  if (hex.length === 4) {
    hex = "#" + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
  }

  try {
    let luminance = getLuminance(hex);
    let ratio = 1.05 / (luminance + 0.05);
    if (ratio >= 3.5) {
      return hex;
    }

    let r = parseInt(hex.slice(1, 3), 16);
    let g = parseInt(hex.slice(3, 5), 16);
    let b = parseInt(hex.slice(5, 7), 16);

    for (let i = 0; i < 10; i++) {
      r = Math.floor(r * 0.85);
      g = Math.floor(g * 0.85);
      b = Math.floor(b * 0.85);

      const nextHex =
        "#" +
        [r, g, b]
          .map((v) => {
            const s = v.toString(16);
            return s.length === 1 ? "0" + s : s;
          })
          .join("");

      luminance = getLuminance(nextHex);
      ratio = 1.05 / (luminance + 0.05);
      if (ratio >= 3.5) {
        return nextHex;
      }
    }
    return defaultFallback;
  } catch {
    return defaultFallback;
  }
}
